import { createElement, Fragment, type ReactNode } from "react";

import type { ScoutToolPart } from "./message/scoutChatMessage";

/** A card rendered from one Scout tool's output, with its type parameter erased. */
export type ScoutToolCard = {
  toolName: string;
  /** Hide the tool's row in the tool-call trace; the card stands in for it. */
  hideTraceRow: boolean;
  renderFor: (toolPart: ScoutToolPart) => ReactNode | null;
};

export function defineScoutToolCard<T>(definition: {
  toolName: string;
  hideTraceRow: boolean;
  /** Null when the output isn't a card (e.g. a failed or unrelated call). */
  parse: (toolOutput: unknown) => T | null;
  render: (value: T) => ReactNode;
}): ScoutToolCard {
  return {
    toolName: definition.toolName,
    hideTraceRow: definition.hideTraceRow,
    renderFor: (toolPart) => {
      const value = definition.parse(toolPart.toolOutput);
      return value === null ? null : definition.render(value);
    },
  };
}

/** Cards for `tools`, grouped in `cards` order and then by tool order. */
export function renderScoutToolCards(
  tools: ReadonlyArray<ScoutToolPart>,
  cards: ReadonlyArray<ScoutToolCard>
): ReactNode[] {
  const rendered: ReactNode[] = [];
  for (const card of cards) {
    for (const toolPart of tools) {
      if (toolPart.toolName !== card.toolName) continue;
      const node = card.renderFor(toolPart);
      if (node === null) continue;
      rendered.push(createElement(Fragment, { key: `${toolPart.id}-${card.toolName}-card` }, node));
    }
  }
  return rendered;
}

export function hiddenTraceToolNames(cards: ReadonlyArray<ScoutToolCard>): ReadonlySet<string> {
  return new Set(cards.filter((card) => card.hideTraceRow).map((card) => card.toolName));
}
